import React, { useState, useEffect } from 'react';
import { Achievement, AchievementStats } from '../types';
import { achievementsAPI } from '../services/achievementsAPI';
import './AchievementsPanel.css';

interface AchievementsPanelProps {
  onClose?: () => void;
}

const AchievementsPanel: React.FC<AchievementsPanelProps> = ({ onClose }) => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [stats, setStats] = useState<AchievementStats | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'unlocked' | 'new'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAchievements();
  }, [activeTab]);

  const loadAchievements = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      switch (activeTab) {
        case 'unlocked':
          response = await achievementsAPI.getUnlocked();
          break;
        case 'new':
          response = await achievementsAPI.getNew();
          break;
        default:
          response = await achievementsAPI.getProgress();
          break;
      }

      setAchievements(response.data.data.achievements);
      
      if (response.data.data.stats) {
        setStats(response.data.data.stats);
      } else if (activeTab === 'all') {
        // Cargar estadísticas por separado si no vienen en la respuesta
        const statsResponse = await achievementsAPI.getStats();
        setStats(statsResponse.data.data.stats);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsSeen = async (achievementId: number) => {
    try {
      await achievementsAPI.markAsSeen(achievementId);
      // Actualizar el achievement en la lista
      setAchievements(prev =>
        prev.map(achievement =>
          achievement.id === achievementId
            ? { ...achievement, is_new: false }
            : achievement
        )
      );
    } catch (err: any) {
      console.error('Error marking achievement as seen:', err);
    }
  };

  const handleMarkAllAsSeen = async () => {
    try {
      await achievementsAPI.markAllAsSeen();
      setAchievements(prev =>
        prev.map(achievement => ({ ...achievement, is_new: false }))
      );
      if (stats) {
        setStats({ ...stats, new_achievements: 0 });
      }
    } catch (err: any) {
      console.error('Error marking all achievements as seen:', err);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return '#10b981';
      case 'rare': return '#3b82f6';
      case 'epic': return '#8b5cf6';
      case 'legendary': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getRarityText = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'Común';
      case 'rare': return 'Raro';
      case 'epic': return 'Épico';
      case 'legendary': return 'Legendario';
      default: return 'Común';
    }
  };

  if (loading) {
    return (
      <div className="achievements-panel">
        <div className="panel-header">
          <h2>🏆 Logros</h2>
          {onClose && (
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando logros...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievements-panel">
        <div className="panel-header">
          <h2>🏆 Logros</h2>
          {onClose && (
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
        <div className="error-container">
          <p>Error: {error}</p>
          <button onClick={loadAchievements} className="retry-btn">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="achievements-panel">
      <div className="panel-header">
        <h2>🏆 Logros</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        )}
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="achievements-stats">
          <div className="stat-item">
            <span className="stat-value">{stats.unlocked_achievements}</span>
            <span className="stat-label">Desbloqueados</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.total_points}</span>
            <span className="stat-label">Puntos</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.completion_percentage}%</span>
            <span className="stat-label">Progreso</span>
          </div>
          {stats.new_achievements > 0 && (
            <div className="stat-item new">
              <span className="stat-value">{stats.new_achievements}</span>
              <span className="stat-label">Nuevos</span>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="achievements-tabs">
        <button
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          Todos ({achievements.length})
        </button>
        <button
          className={`tab ${activeTab === 'unlocked' ? 'active' : ''}`}
          onClick={() => setActiveTab('unlocked')}
        >
          Desbloqueados
        </button>
        {stats && stats.new_achievements > 0 && (
          <button
            className={`tab ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            Nuevos ({stats.new_achievements})
          </button>
        )}
      </div>

      {/* Botón para marcar todos como vistos */}
      {activeTab === 'new' && achievements.length > 0 && (
        <div className="actions-bar">
          <button onClick={handleMarkAllAsSeen} className="mark-all-btn">
            Marcar todos como vistos
          </button>
        </div>
      )}

      {/* Lista de achievements */}
      <div className="achievements-list">
        {achievements.length === 0 ? (
          <div className="empty-state">
            <p>
              {activeTab === 'unlocked'
                ? 'Aún no has desbloqueado ningún logro'
                : activeTab === 'new'
                ? 'No tienes logros nuevos'
                : 'No se encontraron logros'}
            </p>
          </div>
        ) : (
          achievements.map((achievement) => (
            <div
              key={achievement.id}
              className={`achievement-card ${achievement.is_unlocked ? 'unlocked' : 'locked'} ${
                achievement.is_new ? 'new' : ''
              }`}
              style={{ '--rarity-color': getRarityColor(achievement.rarity) } as React.CSSProperties}
            >
              <div className="achievement-icon">
                {achievement.is_unlocked ? achievement.icon : '🔒'}
              </div>
              
              <div className="achievement-content">
                <div className="achievement-header">
                  <h3 className="achievement-name">{achievement.name}</h3>
                  <div className="achievement-meta">
                    <span className="achievement-rarity" style={{ color: getRarityColor(achievement.rarity) }}>
                      {getRarityText(achievement.rarity)}
                    </span>
                    <span className="achievement-points">{achievement.points} pts</span>
                  </div>
                </div>
                
                <p className="achievement-description">
                  {achievement.is_unlocked ? achievement.description : '???'}
                </p>
                
                {achievement.is_unlocked && achievement.unlocked_at && (
                  <div className="achievement-date">
                    Desbloqueado: {new Date(achievement.unlocked_at).toLocaleDateString('es-ES')}
                  </div>
                )}
              </div>

              {achievement.is_new && (
                <button
                  className="mark-seen-btn"
                  onClick={() => handleMarkAsSeen(achievement.id)}
                  title="Marcar como visto"
                >
                  ✓
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AchievementsPanel;